import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Terms of Service | AI StoryCraft Studio</title>
        <meta
          name="description"
          content="Read the terms for using AI StoryCraft Studio, including billing, cancellation, acceptable use, and ownership rules."
        />
        <link rel="canonical" href="https://ai-storycraft-studio.com/terms-of-service" />
        <meta property="og:title" content="Terms of Service | AI StoryCraft Studio" />
        <meta
          property="og:description"
          content="Read the terms for using AI StoryCraft Studio, including billing, cancellation, acceptable use, and ownership rules."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://ai-storycraft-studio.com/terms-of-service" />
        <meta property="og:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | AI StoryCraft Studio" />
        <meta
          name="twitter:description"
          content="Read the terms for using AI StoryCraft Studio, including billing, cancellation, acceptable use, and ownership rules."
        />
        <meta name="twitter:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
      </Helmet>
      <Navbar />
      <main className="container mx-auto px-4 md:px-6 py-12 md:py-20 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">AI StoryCraft Studio — Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: March 2026</p>

        <div className="prose prose-invert max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="font-display text-xl font-semibold mb-2">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">By accessing or using AI StoryCraft Studio ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">AI StoryCraft Studio provides AI-powered tools for content creators, including script generation, digital product creation, keyword research, video clipping, email list building, sales page generation, and launch planning. Access is provided via a subscription plan billed monthly or annually through Stripe.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">3. Subscription and Billing</h2>
            <p className="text-muted-foreground leading-relaxed">Subscriptions are billed in advance on a monthly or annual basis. You authorise AI StoryCraft Studio to charge your payment method on a recurring basis. Credits are allocated per plan and reset at the start of each billing cycle. Unused credits do not roll over unless otherwise stated.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">4. Refund Policy</h2>
            <p className="text-muted-foreground leading-relaxed">Due to the nature of our digital products and the instant access granted to all tools and training materials upon purchase, all sales are final. No refunds will be issued after a subscription payment has been processed. Please review the plan details carefully before purchasing to ensure the Platform is the right fit for you. If you experience a technical issue that prevents you from accessing the Platform, contact us at support@ai-storycraft-studio.com and we will work to resolve it promptly.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">5. Cancellation</h2>
            <p className="text-muted-foreground leading-relaxed">You may cancel your subscription at any time via the billing portal in your account dashboard. Cancellation takes effect at the end of the current billing period. You will retain access to the Platform until that date.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">6. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">You agree not to use the Platform to generate content that is illegal, harmful, defamatory, or in violation of any third-party rights. You are solely responsible for the content you create and publish using the Platform.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">7. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">All content you generate using the Platform's AI tools is owned by you. AI StoryCraft Studio retains ownership of the Platform itself, including its design, code, and underlying models.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">8. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">AI StoryCraft Studio is provided "as is". To the maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential damages arising from your use of the Platform.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">9. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">These Terms are governed by the laws of England and Wales.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold mb-2">10. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">For questions about these Terms, contact us at support@ai-storycraft-studio.com.</p>
          </section>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
